import { useState } from 'react';
import { Stack, Title, Text, Button, ThemeIcon } from '@mantine/core';
import { IconMail, IconArrowRight } from '@tabler/icons-react';
import { useAuth } from '../../hooks/useAuth';

type EmailVerificationStepProps = {
  value: any;
  onChange: (value: any) => void;
  onNext: () => void;
  onBack: () => void;
  isLastStep: boolean;
  isFirstStep?: boolean;
  isCompleting?: boolean;
};

export const EmailVerificationStep = ({ onNext }: EmailVerificationStepProps) => {
  const { user, resendVerificationEmail } = useAuth();
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);
  const [notVerified, setNotVerified] = useState(false);

  const handleResend = async () => {
    setResending(true);
    setResendError(null);
    try {
      await resendVerificationEmail();
      setResent(true);
    } catch (error) {
      setResendError(error instanceof Error ? error.message : 'Could not send the email. Please try again.');
    } finally {
      setResending(false);
    }
  };

  const handleCheckVerification = async () => {
    if (!user) return;
    setChecking(true);
    setNotVerified(false);
    try {
      await user.reload();
      if (user.emailVerified) {
        // Refresh the ID token so its email_verified claim matches.
        await user.getIdToken(true);
        onNext();
      } else {
        setNotVerified(true);
      }
    } catch {
      setNotVerified(true);
    } finally {
      setChecking(false);
    }
  };

  return (
    <Stack gap="xl" w={{ base: '100%', sm: 700 }} mx="auto" px={{ base: 'sm', sm: 'md' }}>
      <Stack gap="md" ta="center" align="center">
        <ThemeIcon size={80} radius="xl" variant="gradient" gradient={{ from: 'orange', to: 'red' }}>
          <IconMail size={40} />
        </ThemeIcon>
        <Stack gap="xs" align="center">
          <Title order={2} size="h1" fw={700} c="dark">
            Verify your email
          </Title>
          <Text size="lg" c="dimmed" ta="center" maw={400}>
            We sent a verification link to <strong>{user?.email}</strong>. Please check your inbox and click the link, then come back here.
          </Text>
          {notVerified && (
            <Text size="sm" c="red">
              Email not yet verified. Please check your inbox and try again.
            </Text>
          )}
        </Stack>
      </Stack>

      <Stack gap="md" align="center" pt="md">
        <Button
          size="md"
          color="orange"
          rightSection={<IconArrowRight size={18} />}
          onClick={handleCheckVerification}
          loading={checking}
        >
          I've verified my email
        </Button>
        <Button
          variant="subtle"
          size="xs"
          color="orange"
          onClick={handleResend}
          loading={resending}
          disabled={resent}
        >
          {resent ? 'Email resent!' : 'Resend email'}
        </Button>
        {resendError && (
          <Text size="sm" c="red" ta="center">
            {resendError}
          </Text>
        )}
      </Stack>
    </Stack>
  );
};
